import { useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Badge } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { IconArrowLeft, IconPlus, IconPencil, IconStar, IconStarFilled, IconStarOff, IconTrash, IconInfoCircle } from '@tabler/icons-react'
import { toast } from 'react-toastify'
import PortalStandardComponent from '@/components/portal/PortalStandardComponent.tsx'
import LoadBig from '@/components/LoadBig/LoadBig.tsx'
import WatermarkPreview from '@/components/portal/WatermarkPreview.tsx'
import WatermarkEditorModal from '@/components/portal/WatermarkEditorModal.tsx'
import { DeleteConfirmModal, useDeleteConfirmModal } from '@/components/portal/DeleteConfirmModal.tsx'
import { useClient } from '@/stores/useClient.ts'
import { HeyDriveWatermark } from '@/models/heyDriveWatermark.ts'
import { ProfProject } from '@/models/profProject.ts'

export default function ProjectWatermarksPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const { getProject, getWatermarks, deleteWatermark, activateDriveWatermark } = useClient()
  const queryClient = useQueryClient()

  const editorModalRef = useRef<HTMLDialogElement>(null)
  const deleteConfirm = useDeleteConfirmModal()
  const [editing, setEditing] = useState<HeyDriveWatermark | null>(null)
  const [pendingDelete, setPendingDelete] = useState<HeyDriveWatermark | null>(null)

  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const res = await getProject(projectId!)
      return res.data as ProfProject
    },
    enabled: !!projectId,
  })

  const driveId = projectQuery.data?.drive.id
  const activeWatermarkId = projectQuery.data?.drive.watermark ?? null

  // Watermarks are account-global — one library, activatable per project.
  const watermarksQuery = useQuery({
    queryKey: ['watermarks'],
    queryFn: async () => {
      const res = await getWatermarks()
      return res.data as HeyDriveWatermark[]
    },
  })

  const watermarks = watermarksQuery.data ?? []
  const isFirstWatermark = watermarks.length === 0

  const activateMutation = useMutation({
    mutationFn: async (watermarkId: string | null) => {
      const res = await activateDriveWatermark(driveId!, watermarkId)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      queryClient.invalidateQueries({ queryKey: ['watermarks'] })
      toast('Aktives Wasserzeichen geändert. Bestehende Bilder werden im Hintergrund aktualisiert.', { closeButton: true, autoClose: 4000 })
    },
    onError: () => toast.error('Aktivierung fehlgeschlagen'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (wm: HeyDriveWatermark) => {
      if (wm.id === activeWatermarkId) {
        await activateDriveWatermark(driveId!, null)
      }
      await deleteWatermark(wm.id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      queryClient.invalidateQueries({ queryKey: ['watermarks'] })
      toast('Wasserzeichen gelöscht', { closeButton: true, autoClose: 2000 })
      deleteConfirm.close()
      setPendingDelete(null)
    },
    onError: () => toast.error('Löschen fehlgeschlagen'),
  })

  function openCreate() {
    setEditing(null)
    setTimeout(() => editorModalRef.current?.showModal(), 0)
  }

  function openEdit(wm: HeyDriveWatermark) {
    setEditing(wm)
    setTimeout(() => editorModalRef.current?.showModal(), 0)
  }

  function askDelete(wm: HeyDriveWatermark) {
    setPendingDelete(wm)
    setTimeout(() => deleteConfirm.show(), 0)
  }

  const editorIsActive = useMemo(() => {
    if (!editing) return false
    return editing.id === activeWatermarkId
  }, [editing, activeWatermarkId])

  if (projectQuery.isLoading) return <LoadBig />
  if (projectQuery.error || !projectQuery.data) {
    return (
      <div className="container mx-auto p-4">
        <p className="text-error">Fehler beim Laden des Projekts</p>
      </div>
    )
  }

  const project = projectQuery.data

  return (
    <>
      <PortalStandardComponent
        title="Wasserzeichen"
        description={project.title}
        noCard
        actions={
          <>
            <Link to={`/portal/projects/${projectId}?tab=settings`}>
              <Button color="ghost" startIcon={<IconArrowLeft size={18} />}>Zurück</Button>
            </Link>
            <Button color="accent" onClick={openCreate} startIcon={<IconPlus size={18} />}>
              Neues Wasserzeichen
            </Button>
          </>
        }
      >
        <div className="bg-neutral border border-[var(--portal-border)] rounded-lg p-4 mb-4 flex gap-3 items-start">
          <IconInfoCircle size={20} className="opacity-60 flex-shrink-0 mt-0.5" />
          <p className="text-sm opacity-80">
            Deine Originalbilder bleiben unverändert erhalten. Das Wasserzeichen wird nur auf Vorschau- und Download-Versionen für Kunden angewendet. In dieser Projektansicht siehst du weiterhin die Originale.
          </p>
        </div>

        {watermarksQuery.isLoading && <LoadBig />}

        {!watermarksQuery.isLoading && watermarks.length === 0 && (
          <div className="bg-neutral border border-dashed border-[var(--portal-border)] rounded-lg p-10 flex flex-col items-center gap-4">
            <p className="opacity-70">Noch kein Wasserzeichen vorhanden.</p>
            <Button color="accent" onClick={openCreate} startIcon={<IconPlus size={18} />}>
              Wasserzeichen anlegen
            </Button>
          </div>
        )}

        {watermarks.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {watermarks.map((wm) => {
              const isActive = wm.id === activeWatermarkId
              return (
                <div
                  key={wm.id}
                  className="bg-neutral border border-[var(--portal-border)] rounded-lg p-4 flex flex-col gap-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex flex-col min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold truncate">{wm.name || wm.file_name}</span>
                        {isActive && (
                          <Badge color="primary" size="sm" className="gap-1">
                            <IconStarFilled size={12} /> Aktiv
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs opacity-60 truncate">{wm.file_name}</span>
                    </div>
                  </div>

                  <div className="bg-secondary rounded overflow-hidden flex items-center justify-center">
                    <WatermarkPreview
                      watermarkUrl={wm.file}
                      posX={wm.pos_x}
                      posY={wm.pos_y}
                      picOriginX={wm.pic_origin_x}
                      picOriginY={wm.pic_origin_y}
                      sizeRatio={wm.size_ratio}
                      rotation={wm.rotation}
                      opacity={wm.opacity}
                      className="max-h-64"
                    />
                  </div>

                  <div className="flex flex-wrap gap-2 justify-end">
                    {isActive ? (
                      <Button
                        color="ghost"
                        size="sm"
                        onClick={() => activateMutation.mutate(null)}
                        loading={activateMutation.isPending}
                        startIcon={<IconStarOff size={16} />}
                      >
                        Deaktivieren
                      </Button>
                    ) : (
                      <Button
                        color="ghost"
                        size="sm"
                        onClick={() => activateMutation.mutate(wm.id)}
                        loading={activateMutation.isPending}
                        startIcon={<IconStar size={16} />}
                      >
                        Aktivieren
                      </Button>
                    )}
                    <Button color="ghost" size="sm" onClick={() => openEdit(wm)} startIcon={<IconPencil size={16} />}>
                      Bearbeiten
                    </Button>
                    <Button color="ghost" size="sm" onClick={() => askDelete(wm)} startIcon={<IconTrash size={16} />}>
                      Löschen
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </PortalStandardComponent>

      {driveId && (
        <WatermarkEditorModal
          modalRef={editorModalRef}
          watermark={editing}
          driveId={driveId}
          projectId={projectId!}
          isFirstWatermark={isFirstWatermark}
          isActive={editorIsActive}
        />
      )}

      <DeleteConfirmModal
        modalRef={deleteConfirm.modalRef}
        onConfirm={() => pendingDelete && deleteMutation.mutate(pendingDelete)}
        isLoading={deleteMutation.isPending}
        title="Wasserzeichen löschen"
        message={
          pendingDelete?.id === activeWatermarkId
            ? 'Dieses Wasserzeichen ist aktiv. Beim Löschen wird es von allen Bildern entfernt. Fortfahren?'
            : 'Wasserzeichen wirklich löschen?'
        }
      />
    </>
  )
}
